import { useState, useContext, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { CUSTOMER_PIN_LENGTH, isWeakPin, WEAK_PIN_MESSAGE } from "@shared/pinPolicy";
import { OrganizationContext } from "@/contexts/OrganizationContext";
import { apiRequest } from "@/lib/queryClient";
import freshorderLogo from "../assets/freshorder-logo.png";
import { Loader2, KeyRound, CheckCircle2, XCircle } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function ResetPin() {
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  
  const orgContext = useContext(OrganizationContext);
  const organization = orgContext?.organization;
  const orgSlug = orgContext?.orgSlug;

  const logoSrc = organization?.logoUrl || freshorderLogo;
  const logoAlt = organization?.name || "FreshOrder";

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");

  useEffect(() => {
    if (!token) {
      setIsVerifying(false);
      setTokenError("No reset token provided");
      return;
    }

    setIsVerifying(false);
    setTokenValid(true);
  }, [token]);

  const handleResetPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPin || newPin.length !== CUSTOMER_PIN_LENGTH || newPin !== confirmPin || isWeakPin(newPin) || !token) return;

    setIsLoading(true);
    try {
      const response = await apiRequest("POST", `/api/customer/verify-magic-link`, {
        token,
        newPin
      });
      
      if (response.ok) {
        const { customUrl, requiresSignIn } = await response.json();
        if (requiresSignIn) {
          // Group accounts pick a location after signing in — no session yet.
          toast({
            title: "PIN saved!",
            description: "Sign in with your phone number and new PIN to continue.",
          });
          setLocation(orgSlug ? `/org/${orgSlug}` : "/");
          return;
        }
        toast({
          title: "PIN reset successful!",
          description: "Your PIN has been updated.",
        });
        const redirectPath = orgSlug 
          ? `/org/${orgSlug}/order/${customUrl}`
          : `/${customUrl}`;
        setLocation(redirectPath);
      } else {
        const error = await response.json();
        toast({
          title: "Reset failed",
          description: error.message || "Please try again or request a new link.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to reset PIN. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  const loginPath = orgSlug ? `/org/${orgSlug}` : "/";

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg border border-gray-200">
        <CardHeader className="text-center border-b border-gray-100 bg-gray-50/50 p-6">
          <img 
            src={logoSrc} 
            alt={logoAlt} 
            className="h-14 mx-auto mb-4 object-contain"
            data-testid="img-org-logo"
          />
          <p className="text-sm text-gray-600">
            {isVerifying ? "Verifying link..." : tokenValid ? "Create your new 4-digit PIN" : "Reset link"}
          </p>
        </CardHeader>
        <CardContent className="p-6">
          {isVerifying && (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-4 text-gray-600">Verifying your reset link...</p>
            </div>
          )}

          {!isVerifying && tokenError && (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <p className="text-gray-700 font-medium">Invalid or expired link</p>
              <p className="text-sm text-gray-500">{tokenError}</p>
              <Button
                onClick={() => setLocation(loginPath)}
                className="mt-4"
                data-testid="button-back-to-login"
              >
                Back to Login
              </Button>
            </div>
          )}

          {!isVerifying && tokenValid && (
            <form onSubmit={handleResetPin} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New PIN</label>
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={CUSTOMER_PIN_LENGTH}
                      value={newPin}
                      onChange={setNewPin}
                      data-testid="input-new-pin"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm PIN</label>
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={CUSTOMER_PIN_LENGTH}
                      value={confirmPin}
                      onChange={setConfirmPin}
                      data-testid="input-confirm-pin"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  {confirmPin.length === CUSTOMER_PIN_LENGTH && newPin !== confirmPin && (
                    <p className="text-red-500 text-sm text-center mt-2">PINs don't match</p>
                  )}
                  {newPin.length === CUSTOMER_PIN_LENGTH && isWeakPin(newPin) && (
                    <p className="text-red-500 text-sm text-center mt-2" data-testid="text-weak-pin">{WEAK_PIN_MESSAGE}</p>
                  )}
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 text-lg bg-[#3D8C40] hover:bg-green-600 text-white"
                disabled={newPin.length !== CUSTOMER_PIN_LENGTH || newPin !== confirmPin || isWeakPin(newPin) || isLoading}
                data-testid="button-reset-pin"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <KeyRound className="h-5 w-5 mr-2" />
                    Reset PIN & Continue
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
